import { Controller } from "@hotwired/stimulus"

// Phase 3.3 Tier 2 — no-code mapping picker.
//
// Owns the in-form UX for the flat-file template editor:
//
//   • Three swap-on-kind format panels (CSV / XML / fixed-width)
//   • Sample-file dropzone (drag-drop OR click) → POST parse_sample
//     → render auto-suggested bindings with confidence chips
//   • Drag-and-drop binding picker (left = registry fields w/ samples,
//     right = source columns w/ target_field <select>)
//   • Filename pattern live preview (interpolates {date}/{timestamp}/
//     {project_code}/{sequence})
//   • Preview-before-commit panel (5s debounce on mapping change,
//     POST preview → render first 10 rendered rows w/ red-highlighted
//     error cells)
//
// Persists the mapping back to the form via two hidden inputs
// (mapping_json, format_options_json) so the existing controller's
// strong-params parser sees the same shape it always has.
export default class extends Controller {
  static targets = [
    "kindSelect", "resourceTypeSelect", "filenameInput", "filenamePreview",
    "formatPanel", "csvOption", "xmlOption", "fixedWidthOption",
    "dropzone", "dropzoneLabel", "dropzoneSubLabel", "sampleInput", "parseStatus",
    "bindingsHeader", "bindingsList", "bindingsEmpty",
    "mappingInput", "formatOptionsInput",
    "previewProject", "previewButton", "previewStatus", "previewTable",
    "resourceLabel", "referenceList",
    // § 3.11 Tier 1 — "Use this template as a starting point" picker.
    "seedPanel", "seedSelect", "seedStatus", "seedDescription"
  ]
  static values = {
    resourceType: String, kind: String,
    parseUrl: String, previewUrl: String,
    mapping: String, formatOptions: String,
    outboundFields: String, inboundFields: String,
    transforms: String,
    // § 3.11 Tier 1 — seed-pack picker URLs. The index URL fetches the
    // seed list (filtered server-side by provider slug); the show URL
    // template substitutes `__SEED_ID__` for the chosen seed_id.
    seedsIndexUrl: String,
    seedShowUrlTemplate: String,
    // § 3.11 Tier 2 — landing partials deep-link into the New Template
    // form with `?seed=<seed_id>`; after `_loadSeedList` populates the
    // select, the controller selects the matching option and dispatches
    // a change event so `loadSeed` fires automatically.
    preselectSeed: String
  }

  connect() {
    this._mapping = this._safeParse(this.mappingValue, [])
    this._formatOptions = this._safeParse(this.formatOptionsValue, {})
    this._outboundFields = this._safeParse(this.outboundFieldsValue, [])
    this._inboundFields = this._safeParse(this.inboundFieldsValue, [])
    this._transforms = this._safeParse(this.transformsValue, [])
    this._sourceColumns = this._mapping.map(b => b.source_field).filter(Boolean)
    this._previewTimer = null

    this._renderReferenceList()
    this._renderBindings()
    this._syncFormatPanel()
    this._renderFilenamePreview()
    this._writeHiddenInputs()
    // § 3.11 Tier 1 — populate the seed-pack picker on connect. Hidden
    // when the connection's provider has no seeds.
    this._loadSeedList()
  }

  // ── Top-level field changes ──────────────────────────────────────

  kindChanged() {
    this.kindValue = this.kindSelectTarget.value
    this._syncFormatPanel()
    this._maybeChangeFilenameExtension()
    this._writeHiddenInputs()
    this._scheduleAutoPreview()
  }

  resourceTypeChanged() {
    const newType = this.resourceTypeSelectTarget.value
    if (newType === this.resourceTypeValue) return
    // Switching resource type invalidates target-field bindings — drop
    // any binding whose target isn't valid for the new type. The
    // server-injected outbound/inbound field lists are scoped to the
    // ORIGINAL resource type, so after a type change the picker shows
    // a stale set until the admin saves and the form reloads. The
    // server-side validator catches anything that slipped through.
    const allowed = new Set(this._fieldsForCurrentDirection().map(f => f.key))
    this._mapping = this._mapping.filter(b => !b.target_field || allowed.has(b.target_field))
    this.resourceTypeValue = newType
    this._renderReferenceList()
    this._renderBindings()
    this._writeHiddenInputs()
    this._scheduleAutoPreview()
  }

  filenameChanged() {
    this._renderFilenamePreview()
  }

  formatOptionChanged(event) {
    const target = event.currentTarget
    const key = target.dataset.optionKey
    if (!key) return
    let value
    if (target.type === "checkbox") {
      value = target.checked
    } else if (target.type === "select-one" && target.value === "\\r\\n") {
      value = "\r\n"
    } else if (target.type === "select-one" && target.value === "\\n") {
      value = "\n"
    } else {
      value = target.value
    }
    this._formatOptions[key] = value
    this._writeHiddenInputs()
    this._scheduleAutoPreview()
  }

  // ── § 3.11 Tier 1 — seed-pack picker ─────────────────────────────
  //
  // The "Use this template as a starting point" select on the New /
  // Edit Template form. `_loadSeedList` runs at connect to populate
  // the dropdown via the server-side provider-slug filter; if the
  // list is empty, the panel hides itself. `loadSeed` runs on select
  // change — fetches the full seed mapping JSON and overwrites the
  // form's bindings + format_options + filename_pattern + kind +
  // resource_type. Any unsaved customer edits are clobbered (the
  // panel's blue-tinted callout signals this is a starting-point
  // import — customers expecting to preserve mid-edit state would
  // save before importing).

  async _loadSeedList() {
    if (!this.hasSeedPanelTarget) return
    const url = this.seedsIndexUrlValue
    if (!url) return
    try {
      const csrf = document.querySelector("meta[name='csrf-token']")?.content
      const res = await fetch(url, {
        method: "GET",
        headers: { "Accept": "application/json", "X-CSRF-Token": csrf || "", "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin"
      })
      const json = await res.json()
      if (!res.ok || !json.ok) return
      const seeds = Array.isArray(json.seeds) ? json.seeds : []
      if (seeds.length === 0) {
        this.seedPanelTarget.classList.add("hidden")
        return
      }
      this._seedSummaries = seeds
      // Reset the select then append one <option> per seed.
      const select = this.seedSelectTarget
      while (select.options.length > 1) select.remove(1)
      seeds.forEach(seed => {
        const opt = document.createElement("option")
        opt.value = seed.seed_id
        opt.textContent = seed.name
        select.appendChild(opt)
      })
      this.seedPanelTarget.classList.remove("hidden")
      // § 3.11 Tier 2 — `?seed=<seed_id>` deep-link from a landing
      // partial preselects the option and fires `loadSeed`.
      const preselect = (this.hasPreselectSeedValue ? this.preselectSeedValue : "").trim()
      if (preselect && seeds.some(s => s.seed_id === preselect)) {
        select.value = preselect
        select.dispatchEvent(new Event("change", { bubbles: true }))
      }
    } catch (err) {
      console.error("[flat-file-template-form] seed list load failed", err)
      // Silent failure — picker is optional convenience, not load-bearing.
      this.seedPanelTarget.classList.add("hidden")
    }
  }

  async loadSeed(event) {
    const seedId = (event?.currentTarget?.value || "").trim()
    if (!seedId) {
      this._setSeedStatus("", "")
      if (this.hasSeedDescriptionTarget) this.seedDescriptionTarget.textContent = ""
      return
    }
    const tpl = this.seedShowUrlTemplateValue
    if (!tpl) return
    const url = tpl.replace("__SEED_ID__", encodeURIComponent(seedId))
    this._setSeedStatus("Loading…", "info")
    try {
      const csrf = document.querySelector("meta[name='csrf-token']")?.content
      const res = await fetch(url, {
        method: "GET",
        headers: { "Accept": "application/json", "X-CSRF-Token": csrf || "", "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin"
      })
      const json = await res.json()
      if (!res.ok || !json.ok || !json.seed) {
        this._setSeedStatus(json?.message || `Load failed (HTTP ${res.status}).`, "error")
        return
      }
      this._applySeed(json.seed)
      this._setSeedStatus(`Imported "${json.seed.name}" — edit before saving.`, "ok")
      if (this.hasSeedDescriptionTarget) {
        this.seedDescriptionTarget.textContent = json.seed.description || ""
      }
    } catch (err) {
      console.error("[flat-file-template-form] seed load failed", err)
      this._setSeedStatus("Load failed — check the network and retry.", "error")
    }
  }

  _applySeed(seed) {
    // Match the server-side persisted shape: kind / resource_type /
    // filename_pattern as top-level form fields; format_options +
    // mapping as the picker's Hash + Array shapes.
    if (seed.kind && this.hasKindSelectTarget) {
      this.kindSelectTarget.value = seed.kind
      this.kindValue = seed.kind
    }
    if (seed.resource_type && this.hasResourceTypeSelectTarget) {
      this.resourceTypeSelectTarget.value = seed.resource_type
      this.resourceTypeValue = seed.resource_type
    }
    if (seed.filename_pattern != null && this.hasFilenameInputTarget) {
      this.filenameInputTarget.value = seed.filename_pattern
    }
    // Override the form's name input ONLY when the input is currently
    // empty — we never clobber an admin-typed name.
    const nameInput = this.element.querySelector("input[name='integration_flat_file_template[name]']")
    if (nameInput && !nameInput.value.trim() && seed.name) {
      nameInput.value = seed.name
    }
    this._formatOptions = (seed.format_options && typeof seed.format_options === "object") ? seed.format_options : {}
    const mapping = Array.isArray(seed.mapping) ? seed.mapping : []
    this._mapping = mapping.map(b => ({
      source_field: b.source_field || "",
      target_field: b.target_field || "",
      transform:    b.transform || "none",
      default:      b.default || "",
      format_options: b.format_options && typeof b.format_options === "object" ? b.format_options : null
    }))
    this._sourceColumns = this._mapping.map(b => b.source_field).filter(Boolean)
    this._renderReferenceList()
    this._renderBindings()
    this._syncFormatPanel()
    this._renderFilenamePreview()
    this._writeHiddenInputs()
    this._scheduleAutoPreview()
  }

  _setSeedStatus(message, level) {
    if (!this.hasSeedStatusTarget) return
    const colors = { ok: "text-emerald-700", info: "text-blue-700", warn: "text-amber-700", error: "text-rose-700" }
    this.seedStatusTarget.textContent = message
    this.seedStatusTarget.className = `text-[11px] ${colors[level] || "text-blue-700"}`
  }

  // ── Sample dropzone ──────────────────────────────────────────────

  openSamplePicker(event) {
    if (event) event.preventDefault()
    this.sampleInputTarget.click()
  }

  sampleSelected(event) {
    const file = event.currentTarget.files?.[0]
    if (!file) return
    this._parseSample(file)
  }

  highlight(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("ring-2", "ring-brand", "bg-blue-50")
  }

  unhighlight(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("ring-2", "ring-brand", "bg-blue-50")
  }

  droppedSample(event) {
    event.preventDefault()
    this.unhighlight(event)
    const file = event.dataTransfer?.files?.[0]
    if (!file) return
    // Sync into the file input so the form submit carries the upload.
    const dt = new DataTransfer()
    dt.items.add(file)
    this.sampleInputTarget.files = dt.files
    this._parseSample(file)
  }

  async _parseSample(file) {
    const url = this.parseUrlValue
    if (!url) {
      this._setParseStatus("Save the template once before uploading a sample.", "warn")
      return
    }
    this._setParseStatus(`Parsing ${file.name}…`, "info")

    const fd = new FormData()
    fd.append("integration_flat_file_template[sample_file]", file)
    fd.append("has_header", "true")

    const csrf = document.querySelector("meta[name='csrf-token']")?.content
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Accept": "application/json", "X-CSRF-Token": csrf || "", "X-Requested-With": "XMLHttpRequest" },
        body: fd,
        credentials: "same-origin"
      })
      const json = await res.json()
      if (!res.ok || !json.ok) {
        this._setParseStatus(json.message || `Parse failed (HTTP ${res.status}).`, "error")
        return
      }
      this._sourceColumns = json.source_columns || []
      this._applySuggestions(json.suggestions || [], json.first_row || [])
      this._setParseStatus(
        `Found ${this._sourceColumns.length} columns · ~${json.row_count_estimate || 0} data rows · ${this._highCount(json.suggestions)} high-confidence matches.`,
        "ok"
      )
      if (this.hasDropzoneSubLabelTarget) {
        this.dropzoneSubLabelTarget.textContent = `${file.name} (${(file.size / 1024).toFixed(1)} KB)`
      }
    } catch (err) {
      console.error("[flat-file-template-form] sample parse failed", err)
      this._setParseStatus("Parse failed — check the file and retry.", "error")
    }
  }

  _highCount(suggestions) {
    return (suggestions || []).filter(s => s.confidence === "high").length
  }

  _applySuggestions(suggestions, firstRow) {
    // Replace existing bindings with the suggestions (preserving any
    // bindings whose source_field already had a hand-edited target —
    // we don't clobber explicit overrides).
    const existingByKey = new Map(this._mapping.map(b => [String(b.source_field).toLowerCase(), b]))
    const next = suggestions.map((s, idx) => {
      const existing = existingByKey.get(String(s.source_field).toLowerCase())
      return {
        source_field: s.source_field,
        target_field: existing?.target_field || s.target_field || "",
        transform:    existing?.transform    || s.transform || "none",
        default:      existing?.default      || "",
        _confidence:  s.confidence || null,
        _sample:      firstRow[idx] || ""
      }
    })
    this._mapping = next
    this._renderBindings()
    this._writeHiddenInputs()
    this._scheduleAutoPreview()
  }

  _setParseStatus(message, level) {
    if (!this.hasParseStatusTarget) return
    const colors = {
      info:  "bg-blue-50 text-blue-700",
      ok:    "bg-emerald-50 text-emerald-700",
      warn:  "bg-amber-50 text-amber-700",
      error: "bg-rose-50 text-rose-700"
    }
    const t = this.parseStatusTarget
    t.classList.remove("hidden", ...Object.values(colors).flatMap(c => c.split(" ")))
    const cls = (colors[level] || colors.info).split(" ")
    t.classList.add(...cls)
    t.textContent = message
  }

  // ── Bindings picker ──────────────────────────────────────────────

  addBinding(event) {
    if (event) event.preventDefault()
    this._mapping.push({ source_field: "", target_field: "", transform: "none", default: "" })
    this._renderBindings()
    this._writeHiddenInputs()
  }

  bindingChanged(event) {
    const row = event.currentTarget.closest("[data-binding-index]")
    if (!row) return
    const idx = parseInt(row.dataset.bindingIndex, 10)
    if (isNaN(idx)) return
    const field = event.currentTarget.dataset.bindingField
    if (!field) return
    let value = event.currentTarget.value
    if (field === "target_field") {
      // Default the transform to the field's natural type (date → iso date).
      const targetField = this._fieldsForCurrentDirection().find(f => f.key === value)
      if (targetField && this._mapping[idx].transform === "none") {
        if (targetField.type === "date" || targetField.type === "datetime") {
          this._mapping[idx].transform = "iso8601_date"
        }
      }
    }
    this._mapping[idx][field] = value
    this._writeHiddenInputs()
    this._scheduleAutoPreview()
    if (field === "target_field") this._renderBindings() // re-render to update transform default
  }

  removeBinding(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("[data-binding-index]")
    if (!row) return
    const idx = parseInt(row.dataset.bindingIndex, 10)
    if (isNaN(idx)) return
    this._mapping.splice(idx, 1)
    this._renderBindings()
    this._writeHiddenInputs()
    this._scheduleAutoPreview()
  }

  moveBindingUp(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("[data-binding-index]")
    const idx = parseInt(row?.dataset.bindingIndex, 10)
    if (isNaN(idx) || idx <= 0) return
    const tmp = this._mapping[idx - 1]
    this._mapping[idx - 1] = this._mapping[idx]
    this._mapping[idx] = tmp
    this._renderBindings()
    this._writeHiddenInputs()
    this._scheduleAutoPreview()
  }

  moveBindingDown(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("[data-binding-index]")
    const idx = parseInt(row?.dataset.bindingIndex, 10)
    if (isNaN(idx) || idx >= this._mapping.length - 1) return
    const tmp = this._mapping[idx + 1]
    this._mapping[idx + 1] = this._mapping[idx]
    this._mapping[idx] = tmp
    this._renderBindings()
    this._writeHiddenInputs()
    this._scheduleAutoPreview()
  }

  // ── Preview ──────────────────────────────────────────────────────

  async requestPreview(event) {
    if (event) event.preventDefault()
    const url = this.previewUrlValue
    if (!url) {
      this._setPreviewStatus("Save the template once to preview real data.")
      return
    }
    if (this._mapping.length === 0) {
      this._setPreviewStatus("Add at least one binding to preview.")
      return
    }

    this._setPreviewStatus("Loading preview…")
    const projectId = this.hasPreviewProjectTarget ? this.previewProjectTarget.value : ""
    const csrf = document.querySelector("meta[name='csrf-token']")?.content

    const fd = new FormData()
    fd.append("mapping", JSON.stringify(this._mapping))
    fd.append("format_options", JSON.stringify(this._formatOptions))
    if (projectId) fd.append("project_id", projectId)

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Accept": "application/json", "X-CSRF-Token": csrf || "", "X-Requested-With": "XMLHttpRequest" },
        body: fd,
        credentials: "same-origin"
      })
      const json = await res.json()
      if (!res.ok || !json.ok) {
        this._setPreviewStatus(json.message || `Preview failed (HTTP ${res.status}).`)
        return
      }
      this._renderPreviewTable(json)
      const where = json.project_used ? `from project "${json.project_used}"` : "from the most recent matching record"
      this._setPreviewStatus(`Showing ${json.rows.length} of ${json.total_candidates} rows ${where}.`)
    } catch (err) {
      console.error("[flat-file-template-form] preview failed", err)
      this._setPreviewStatus("Preview failed — check the connection and retry.")
    }
  }

  _scheduleAutoPreview() {
    if (!this.hasPreviewTableTarget || !this.previewUrlValue) return
    clearTimeout(this._previewTimer)
    this._previewTimer = setTimeout(() => this.requestPreview(), 5000)
  }

  _setPreviewStatus(text) {
    if (this.hasPreviewStatusTarget) this.previewStatusTarget.textContent = text
  }

  _renderPreviewTable(payload) {
    const target = this.previewTableTarget
    if (!payload.rows || payload.rows.length === 0) {
      target.innerHTML = `<div class="text-[12px] text-amber-700 bg-amber-50 px-3 py-2 rounded">No matching records to preview. Try a project with approved pay requests, or add records of this resource type.</div>`
      return
    }
    const header = (payload.header || []).map(h => `<th class="text-left px-2 py-1 font-medium text-gray-600 border-b border-gray-200">${this._escape(h)}</th>`).join("")
    const rows = payload.rows.map(row => {
      const cells = row.map(cell => {
        const cls = cell.error ? "px-2 py-1 border border-rose-300 bg-rose-50 text-rose-700" :
                    cell.placeholder ? "px-2 py-1 border-b border-gray-100 text-gray-400 italic" :
                                       "px-2 py-1 border-b border-gray-100 text-gray-700"
        const titleAttr = cell.error ? ` title="${this._escape(cell.error)}"` : ""
        const text = cell.error ? this._escape(cell.error) : this._escape(cell.value || "")
        return `<td class="${cls}"${titleAttr}>${text}</td>`
      }).join("")
      return `<tr>${cells}</tr>`
    }).join("")
    target.innerHTML = `<table class="min-w-full text-[12px] font-mono"><thead><tr>${header}</tr></thead><tbody>${rows}</tbody></table>`
  }

  // ── Internals ────────────────────────────────────────────────────

  _safeParse(text, fallback) {
    if (!text || text === "null") return fallback
    try { return JSON.parse(text) } catch { return fallback }
  }

  // Returns the registered fields for the picked direction (outbound
  // when kind starts with `outbound_`, inbound otherwise). Both arrays
  // are server-injected on form load for the CURRENT resource type;
  // changing the resource-type select triggers a re-render that uses
  // whatever's still in memory and prunes invalid bindings. The form
  // is posted + re-fetched if the admin saves with the new type.
  _fieldsForCurrentDirection() {
    const inbound = (this.kindSelectTarget?.value || this.kindValue || "").startsWith("inbound_")
    return inbound ? this._inboundFields : this._outboundFields
  }

  _syncFormatPanel() {
    if (!this.hasFormatPanelTarget) return
    const kind = this.kindSelectTarget.value
    let activeKind
    if (kind === "outbound_csv" || kind === "inbound_csv") activeKind = "csv"
    else if (kind === "outbound_xml" || kind === "inbound_xml") activeKind = "xml"
    else if (kind === "outbound_fixed_width") activeKind = "fixed_width"
    else activeKind = "csv"
    this.formatPanelTargets.forEach(p => {
      p.classList.toggle("hidden", p.dataset.formatKind !== activeKind)
    })
  }

  _renderReferenceList() {
    if (!this.hasReferenceListTarget) return
    if (this.hasResourceLabelTarget) this.resourceLabelTarget.textContent = this.resourceTypeSelectTarget.value
    const fields = this._fieldsForCurrentDirection()
    this.referenceListTarget.innerHTML = fields.map(f =>
      `<li><code>${this._escape(f.key)}</code> <span class="text-gray-400">— ${this._escape(f.label)}</span> <span class="text-gray-500">e.g. ${this._escape(f.sample)}</span></li>`
    ).join("")
  }

  _renderBindings() {
    if (!this.hasBindingsListTarget) return
    if (this._mapping.length === 0) {
      this.bindingsListTarget.innerHTML = ""
      this.bindingsEmptyTarget.classList.remove("hidden")
      this.bindingsHeaderTarget.classList.add("hidden")
      return
    }
    this.bindingsEmptyTarget.classList.add("hidden")
    this.bindingsHeaderTarget.classList.remove("hidden")
    const fields = this._fieldsForCurrentDirection()
    const html = this._mapping.map((b, idx) => this._bindingRowHtml(b, idx, fields)).join("")
    this.bindingsListTarget.innerHTML = html
  }

  _bindingRowHtml(binding, idx, fields) {
    const targetOpts = [`<option value="">— pick a target field —</option>`].concat(
      fields.map(f => `<option value="${this._escape(f.key)}" ${binding.target_field === f.key ? "selected" : ""}>${this._escape(f.label)} · ${this._escape(f.key)}</option>`)
    ).join("")
    const transformOpts = this._transforms.map(t =>
      `<option value="${this._escape(t)}" ${(binding.transform || "none") === t ? "selected" : ""}>${this._escape(t)}</option>`
    ).join("")
    const sourceOpts = this._sourceColumns.length > 0 ?
      [`<option value="${this._escape(binding.source_field)}">${this._escape(binding.source_field)}</option>`].concat(
        this._sourceColumns
          .filter(c => c !== binding.source_field)
          .map(c => `<option value="${this._escape(c)}">${this._escape(c)}</option>`)
      ).join("") : null
    const sourceInput = sourceOpts ?
      `<select data-binding-field="source_field" data-action="change->flat-file-template-form#bindingChanged"
               class="w-full px-2 py-1 text-[12px] border border-gray-200 rounded bg-white cursor-pointer">${sourceOpts}</select>` :
      `<input type="text" value="${this._escape(binding.source_field || "")}"
              data-binding-field="source_field" data-action="input->flat-file-template-form#bindingChanged"
              placeholder="Column name (e.g. Vendor)"
              class="w-full px-2 py-1 text-[12px] border border-gray-200 rounded bg-white">`

    const conf = binding._confidence
    const confChip = !conf ? "" :
      conf === "high"   ? `<span class="inline-block px-1.5 py-0.5 text-[10px] rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">high</span>` :
      conf === "medium" ? `<span class="inline-block px-1.5 py-0.5 text-[10px] rounded-full bg-amber-50 text-amber-700 border border-amber-200">medium</span>` :
                          `<span class="inline-block px-1.5 py-0.5 text-[10px] rounded-full bg-gray-100 text-gray-700 border border-gray-200">low</span>`
    const sampleChip = binding._sample ? `<span class="text-[10px] text-gray-400 italic">(${this._escape(String(binding._sample).slice(0, 40))})</span>` : ""

    return `
      <div class="grid grid-cols-12 gap-2 items-center" data-binding-index="${idx}">
        <div class="col-span-1 flex items-center gap-1">
          <button type="button" data-action="click->flat-file-template-form#moveBindingUp"
                  class="p-0.5 text-gray-400 hover:text-gray-700 cursor-pointer" title="Move up">↑</button>
          <button type="button" data-action="click->flat-file-template-form#moveBindingDown"
                  class="p-0.5 text-gray-400 hover:text-gray-700 cursor-pointer" title="Move down">↓</button>
        </div>
        <div class="col-span-3 space-y-0.5">
          ${sourceInput}
          <div class="flex items-center gap-1">${confChip}${sampleChip}</div>
        </div>
        <div class="col-span-3">
          <select data-binding-field="target_field" data-action="change->flat-file-template-form#bindingChanged"
                  class="w-full px-2 py-1 text-[12px] border border-gray-200 rounded bg-white cursor-pointer">${targetOpts}</select>
        </div>
        <div class="col-span-2">
          <select data-binding-field="transform" data-action="change->flat-file-template-form#bindingChanged"
                  class="w-full px-2 py-1 text-[12px] border border-gray-200 rounded bg-white cursor-pointer">${transformOpts}</select>
        </div>
        <div class="col-span-2">
          <input type="text" value="${this._escape(binding.default || "")}"
                 data-binding-field="default" data-action="input->flat-file-template-form#bindingChanged"
                 placeholder="—"
                 class="w-full px-2 py-1 text-[12px] border border-gray-200 rounded bg-white">
        </div>
        <div class="col-span-1 text-right">
          <button type="button" data-action="click->flat-file-template-form#removeBinding"
                  class="p-1 rounded hover:bg-rose-50 text-rose-600 cursor-pointer" title="Remove">✕</button>
        </div>
      </div>`
  }

  _renderFilenamePreview() {
    if (!this.hasFilenamePreviewTarget) return
    const pattern = this.filenameInputTarget.value || ""
    if (pattern.length === 0) {
      this.filenamePreviewTarget.textContent = "—"
      return
    }
    const now = new Date()
    const pad = n => n.toString().padStart(2, "0")
    const date = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`
    const ts = `${date}T${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
    let out = pattern
      .replace(/\{date\}/g, date)
      .replace(/\{timestamp\}/g, ts)
      .replace(/\{project_code\}/g, "PROJ-001")
      .replace(/\{sequence\}/g, "47")
    // Show what the renderer would do to traversal characters at render time.
    out = out.replace(/\.{2,}/g, "_").replace(/[^A-Za-z0-9_.\-/{}]/g, "")
    this.filenamePreviewTarget.textContent = out
  }

  _maybeChangeFilenameExtension() {
    const kind = this.kindSelectTarget.value
    const pattern = this.filenameInputTarget.value || ""
    let want
    if (kind === "outbound_xml" || kind === "inbound_xml") want = "xml"
    else if (kind === "outbound_fixed_width") want = "txt"
    else want = "csv"
    if (!pattern) {
      this.filenameInputTarget.value = `conslog_export_{date}.${want}`
    } else if (/\.(csv|tsv|xml|txt)$/i.test(pattern)) {
      this.filenameInputTarget.value = pattern.replace(/\.(csv|tsv|xml|txt)$/i, `.${want}`)
    }
    this._renderFilenamePreview()
  }

  _writeHiddenInputs() {
    if (this.hasMappingInputTarget) {
      // Strip presentation-only fields before posting.
      const sanitized = this._mapping.map(b => {
        const { _confidence, _sample, ...rest } = b
        return rest
      })
      this.mappingInputTarget.value = JSON.stringify(sanitized)
    }
    if (this.hasFormatOptionsInputTarget) {
      this.formatOptionsInputTarget.value = JSON.stringify(this._formatOptions)
    }
  }

  _escape(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }
}
